import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { PostingModule, SubledgerType } from '../../../models/financial-account/financial-account';
import {
  ClaimPolicyOption,
  PettyCashFund,
  PettyCashFundBalance,
  PettyCashFundStatus,
  PettyCashFundUpdate,
  UserOption,
  policyOptionOf,
} from '../../../models/petty-cash-fund/petty-cash-fund';
import { PettyCashFundFormModel, pettyCashFundFieldLabels } from '../../../models/petty-cash-fund/petty-cash-fund-form';
import { ReceiptOwnerCode } from '../../../models/expense-receipt/expense-receipt';
import { ApprovalPolicyRoles, PettyCashRoles } from '../../../models/roles/roles-group';
import { PettyCashFundProxyService } from '../../../proxies/petty-cash-fund.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { CURRENCY_OPTIONS, Currency, currencyLabel, currencySymbol } from '../../../utilities/currency.util';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';
import { fundStatusLabel, fundStatusSeverity } from '../petty-cash-fund-status.util';

const LIST_ROUTE = '/petty-cash-funds';
const NEW_ID = 'new';

/**
 * One petty cash fund (#5785): create it as Draft or edit its master data — custodian, members, currency,
 * revolving balances, cash account and the policy that approves its claims. The admin marks it ready for funding,
 * which locks currency and account; the treasurer's opening movement (#5786) then makes it Active.
 */
@Component({
  selector: 'app-petty-cash-fund-details',
  templateUrl: './fund-details.component.html',
})
export class PettyCashFundDetailsComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(PettyCashFundProxyService);
  private readonly userAuth = inject(UserAuthenticationService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly canEdit = this.userAuth.roles.includes(PettyCashRoles.PettyCashFundAdmin);
  readonly canFund = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);
  /** UserMaster's policy list needs ApprovalPolicyViewer/Admin; others see the fund's own policy, without a warning. */
  private readonly canListPolicies =
    this.canEdit &&
    [ApprovalPolicyRoles.ApprovalPolicyViewer, ApprovalPolicyRoles.ApprovalPolicyAdmin].some((role) => this.userAuth.roles.includes(role));
  readonly fieldLabels = pettyCashFundFieldLabels;
  readonly pettyCash = PostingModule.PettyCash;
  /** Bank style (#5785): only accounts with the PettyCashFund subledger can hold a fund (shared ones too). */
  readonly fundSubledger = SubledgerType.PettyCashFund;
  readonly currencyOptions = CURRENCY_OPTIONS.filter((c) => c.value !== Currency.EUR);

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  readonly currentId = signal<string | null>(null);
  readonly fund = signal<PettyCashFund | null>(null);
  readonly balance = signal<PettyCashFundBalance | null>(null);
  /** The account picker reads its read-only state once, so the form renders only when this is set. */
  readonly ready = signal(false);
  readonly users = signal<UserOption[]>([]);
  readonly policies = signal<ClaimPolicyOption[]>([]);

  readonly headerTitle = computed(() =>
    this.isNew() ? this.label('PettyCashFund_New_Title', 'New petty cash fund') : (this.fund()?.name ?? ''),
  );

  readonly saveMenuItems: MenuItem[] = [
    { label: this.label('Save and Close', 'Save and Close'), icon: 'pi pi-save', command: () => this.save(true) },
  ];

  readonly form = new FormGroup<ControlsOf<PettyCashFundFormModel>>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    description: new FormControl<string | null>(null),
    currency: new FormControl<Currency | null>(Currency.HNL, Validators.required),
    custodianUsername: new FormControl<string | null>(null, Validators.required),
    memberUsernames: new FormControl<string[]>([], { nonNullable: true }),
    minRevolvingBalance: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    maxRevolvingBalance: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    glAccountReference: new FormControl<string | null>(null, Validators.required),
    claimPolicyReference: new FormControl<string | null>(null),
  });

  /** Display-only companions the account picker fills in beside the reference. */
  readonly glNumberControl = new FormControl<string | null>(null);
  readonly glNameControl = new FormControl<string | null>(null);

  private readonly selectedCurrency = toSignal(this.form.controls.currency.valueChanges, {
    initialValue: this.form.controls.currency.value,
  });
  /** The input-group addon on the revolving balances; setFund patches the currency silently, so it also tracks the fund. */
  readonly currencyAddon = computed(() => {
    this.selectedCurrency();
    this.fund();
    return currencyLabel(this.form.controls.currency.value) || '—';
  });

  /** Where the balance sits on a 0 – maximum bar, and the minimum's tick on it. */
  readonly balanceBar = computed(() => {
    const balance = this.balance();
    if (_.isNil(balance) || balance.maxRevolvingBalance <= 0) return null;
    const percent = (value: number) => _.clamp((value / balance.maxRevolvingBalance) * 100, 0, 100);
    const level: 'empty' | 'low' | 'ok' =
      balance.currentBalance <= 0 ? 'empty' : balance.currentBalance < balance.minRevolvingBalance ? 'low' : 'ok';
    const color = { empty: 'var(--p-red-500)', low: 'var(--p-orange-500)', ok: 'var(--p-green-500)' }[level];
    return { value: percent(balance.currentBalance), minAt: percent(balance.minRevolvingBalance), level, color };
  });

  private readonly selectedPolicyId = toSignal(this.form.controls.claimPolicyReference.valueChanges, { initialValue: null });
  readonly selectedPolicy = computed(() => {
    const id = this.selectedPolicyId() ?? this.form.controls.claimPolicyReference.value;
    return this.policies().find((p) => p.id === id) ?? null;
  });

  readonly isDraft = computed(() => this.isNew() || this.fund()?.status === PettyCashFundStatus.Draft);
  readonly isClosed = computed(() => this.fund()?.status === PettyCashFundStatus.Closed);
  readonly isActive = computed(() => this.fund()?.status === PettyCashFundStatus.Active);
  /** Only the fund's custodian prepares its expense claims (#5795). */
  readonly isCustodian = computed(
    () => (this.fund()?.custodian.username ?? '').toLowerCase() === (this.userAuth.userLite?.username ?? '-').toLowerCase(),
  );
  readonly currentFundId = computed(() => this.fund()?.id ?? '');
  readonly isReadyForFunding = computed(() => this.fund()?.status === PettyCashFundStatus.ReadyForFunding);
  readonly canMarkReady = computed(() => this.canEdit && !this.isNew() && this.fund()?.status === PettyCashFundStatus.Draft);
  /** The opening panel: filled in while ready for funding, shown read-only once Active. */
  readonly showOpening = computed(() => this.isReadyForFunding() || this.isActive());
  readonly canDelete = computed(() => this.canEdit && !this.isNew() && this.fund()?.status === PettyCashFundStatus.Draft);

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (_.isNil(id)) {
      this.back();
      return;
    }
    this.isNew.set(id === NEW_ID);
    this.currentId.set(id === NEW_ID ? null : id);
    this.loadUsers();
    if (this.canListPolicies) this.loadPolicies();
    if (this.isNew()) {
      this.breadcrumbService.setCurrent('Common_New');
      this.loadDefaultAccount();
    } else {
      this.loadItem(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadUsers(): void {
    this.proxy.getAssignableUsers().subscribe((response) => this.users.set(response.data ?? []));
  }

  private loadPolicies(): void {
    this.proxy.getClaimPolicies().subscribe((response) => this.policies.set(response.data?.items ?? []));
  }

  private loadDefaultAccount(): void {
    this.startLoading();
    this.proxy
      .getDefaultGlAccount()
      .pipe(
        finalize(() => {
          this.stopLoading();
          this.applyEditability();
          this.ready.set(true);
        }),
      )
      .subscribe((response) => {
        const account = response.data;
        if (_.isNil(account)) return;
        this.form.controls.glAccountReference.setValue(account.reference);
        this.glNumberControl.setValue(account.number);
        this.form.markAsPristine();
      });
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const fund = response.data;
        if (!_.isNil(fund)) this.setFund(fund);
      });
    this.loadBalance(id);
  }

  private loadBalance(id: string): void {
    this.proxy.getBalance(id).subscribe((response) => this.balance.set(response.data ?? null));
  }

  private setFund(fund: PettyCashFund): void {
    this.fund.set(fund);
    this.breadcrumbService.setCurrent(fund.number);
    if (!this.canListPolicies)
      this.policies.set(
        policyOptionOf(
          _.isNil(fund.claimPolicyReference) || _.isNil(fund.claimPolicyName)
            ? null
            : { reference: fund.claimPolicyReference, name: fund.claimPolicyName },
        ),
      );
    this.form.patchValue(
      {
        name: fund.name,
        description: fund.description,
        currency: fund.currency,
        custodianUsername: fund.custodian.username,
        memberUsernames: fund.members.map((m) => m.username),
        minRevolvingBalance: fund.minRevolvingBalance,
        maxRevolvingBalance: fund.maxRevolvingBalance,
        glAccountReference: fund.glAccount.reference,
        claimPolicyReference: fund.claimPolicyReference,
      },
      { emitEvent: false },
    );
    this.glNumberControl.setValue(fund.glAccount.number, { emitEvent: false });
    this.applyEditability();
    this.form.markAsPristine();
    this.ready.set(true);
  }

  /** Viewers and closed funds are read-only; currency is fixed once the fund leaves Draft. */
  private applyEditability(): void {
    if (!this.canEdit || this.isClosed()) {
      this.form.disable({ emitEvent: false });
      return;
    }
    this.form.enable({ emitEvent: false });
    if (!this.isDraft()) this.form.controls.currency.disable({ emitEvent: false });
  }

  save(shouldClose = false): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }
    const value = this.form.getRawValue();
    if (_.isNil(value.currency) || _.isNil(value.custodianUsername) || _.isNil(value.glAccountReference)) return;
    const payload: PettyCashFundUpdate = {
      name: value.name.trim(),
      description: value.description?.trim() || null,
      currency: value.currency,
      custodianUsername: value.custodianUsername,
      memberUsernames: value.memberUsernames,
      minRevolvingBalance: value.minRevolvingBalance ?? 0,
      maxRevolvingBalance: value.maxRevolvingBalance ?? 0,
      glAccountReference: value.glAccountReference,
      claimPolicyReference: value.claimPolicyReference,
    };

    const id = this.currentId();
    const request = _.isNil(id) ? this.proxy.create(payload) : this.proxy.update(id, payload);
    this.startLoading();
    request.pipe(finalize(() => this.stopLoading())).subscribe((response) => {
      const saved = response.data;
      if (_.isNil(saved)) return;
      GlobalMessageUtil.success(this.label('PettyCashFund_Title', 'Petty cash funds'), this.label('PettyCashFund_Saved', 'Fund saved.'));
      if (shouldClose) {
        this.back();
        return;
      }
      if (this.isNew()) {
        this.isNew.set(false);
        this.currentId.set(saved.id);
        this.router.navigate([], { queryParams: { id: saved.id }, replaceUrl: true });
      }
      this.setFund(saved);
    });
  }

  /** Hands the saved fund to the treasurer; there is no way back to Draft. */
  confirmReadyForFunding(): void {
    const fund = this.fund();
    if (_.isNil(fund)) return;
    if (this.form.dirty) {
      GlobalMessageUtil.warn(
        this.label('PettyCashFund_Title', 'Petty cash funds'),
        this.label('PettyCashFund_ReadyForFunding_SaveFirst', 'Save your changes before marking the fund ready for funding.'),
      );
      return;
    }
    this.confirmationService.confirm({
      header: this.label('PettyCashFund_ReadyForFunding_Title', 'Ready for funding'),
      message: this.label(
        'PettyCashFund_ReadyForFunding_Message',
        'Send this fund to the treasurer for its opening? Its currency and account can no longer change, and it cannot go back to Draft.',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('PettyCashFund_ReadyForFunding_Accept', 'Mark ready'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .markReadyForFunding(fund.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            if (_.isNil(response.data)) return;
            GlobalMessageUtil.success(
              this.label('PettyCashFund_Title', 'Petty cash funds'),
              this.label('PettyCashFund_ReadyForFunding_Done', 'The fund is ready for funding.'),
            );
            this.reload();
          });
      },
    });
  }

  /** The opening posted: the fund is Active now. */
  onFunded(): void {
    this.reload();
  }

  /** Re-renders the form so the account picker re-reads its read-only state. */
  private reload(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.ready.set(false);
    this.loadItem(id);
  }

  confirmDelete(): void {
    const fund = this.fund();
    if (_.isNil(fund)) return;
    this.confirmationService.confirm({
      header: this.label('PettyCashFund_Delete_Title', 'Delete fund'),
      message: this.label('PettyCashFund_Delete_Message', 'Delete this draft fund? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(fund.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            GlobalMessageUtil.success(
              this.label('PettyCashFund_Title', 'Petty cash funds'),
              this.label('PettyCashFund_Deleted', 'Fund deleted.'),
            );
            this.back();
          });
      },
    });
  }

  /** Receipt capture for this fund (#5794); the API lets only its custodian and members in. */
  captureReceipts(): void {
    const fund = this.fund();
    if (_.isNil(fund)) return;
    this.router.navigate(['/expense-receipts'], { queryParams: { ownerReference: fund.id, ownerCode: ReceiptOwnerCode.PettyCashFund } });
  }

  /** A new expense claim for this fund (#5800). */
  newClaim(): void {
    const fund = this.fund();
    if (_.isNil(fund)) return;
    this.router.navigate(['/expense-claim-details'], {
      queryParams: { id: 'new', ownerReference: fund.id, ownerCode: ReceiptOwnerCode.PettyCashFund },
    });
  }

  /** A new settlement replenishing this fund's posted claims (#5799). */
  replenish(): void {
    const fund = this.fund();
    if (_.isNil(fund)) return;
    this.router.navigate(['/expense-settlement-details'], {
      queryParams: { id: 'new', ownerReference: fund.id, ownerCode: ReceiptOwnerCode.PettyCashFund },
    });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  statusLabel(status: PettyCashFundStatus): string {
    return fundStatusLabel(status, (k, f) => this.label(k, f));
  }

  statusSeverity(status: PettyCashFundStatus): 'success' | 'secondary' | 'warn' | 'info' {
    return fundStatusSeverity(status);
  }

  symbolOf(currency: Currency | null | undefined): string {
    return currencySymbol(currency);
  }

  policyName(policy: ClaimPolicyOption): string {
    return this.translate.currentLang === 'es' ? policy.name.es : policy.name.en;
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
